import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import {
  Client,
  User,
  getClients,
  getVentasUsers,
  assignClientToUser,
  unassignClientFromUser
} from '../services/api';

const Customers: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [ventasUsers, setVentasUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalClients, setTotalClients] = useState(0);
  const [assigningClientId, setAssigningClientId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getClients(page * rowsPerPage, rowsPerPage, debouncedSearch);
      setClients(data.items);
      setTotalClients(data.total);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al cargar clientes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  const loadVentasUsers = async () => {
    try {
      const data = await getVentasUsers();
      setVentasUsers(data);
    } catch (error) {
      console.error('Error loading Ventas users:', error);
    }
  };

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadVentasUsers();
  }, []);

  const handleAssign = async (clientId: string, userId: number) => {
    setAssigningClientId(clientId);
    try {
      await assignClientToUser(clientId, userId);
      setSnackbar({
        open: true,
        message: 'Cliente asignado exitosamente',
        severity: 'success'
      });
      await loadClients();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error al asignar cliente',
        severity: 'error'
      });
    } finally {
      setAssigningClientId(null);
    }
  };

  const handleUnassign = async (clientId: string) => {
    setAssigningClientId(clientId);
    try {
      await unassignClientFromUser(clientId);
      setSnackbar({
        open: true,
        message: 'Cliente desasignado exitosamente',
        severity: 'success'
      });
      await loadClients();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error al desasignar cliente',
        severity: 'error'
      });
    } finally {
      setAssigningClientId(null);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activeClients = clients.filter(c => c.is_active);
  const assignedCount = activeClients.filter(c => c.assigned_to_user_id).length;
  const unassignedCount = activeClients.filter(c => !c.assigned_to_user_id).length;

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Gestión de Clientes
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip label={`Total: ${totalClients}`} color="primary" />
            <Chip label={`Asignados (página): ${assignedCount}`} color="success" />
            <Chip label={`Sin asignar (página): ${unassignedCount}`} color="warning" />
          </Box>
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Buscar cliente (ID, Razón Social, RUC)"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            helperText="La búsqueda se realiza en el servidor - espere 0.5 segundos después de escribir"
          />
        </Paper>

        <Paper>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Razón Social</TableCell>
                  <TableCell>RUC</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Usuario Asignado</TableCell>
                  <TableCell align="right" sx={{ minWidth: 250 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No se encontraron clientes
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow key={client.customer_id}>
                      <TableCell>{client.customer_id}</TableCell>
                      <TableCell>{client.razon_social}</TableCell>
                      <TableCell>{client.ruc}</TableCell>
                      <TableCell>
                        <Chip
                          label={client.is_active ? 'Activo' : 'Inactivo'}
                          color={client.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {client.assigned_to_user ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {client.assigned_to_user.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.assigned_to_user.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip label="Sin asignar" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {client.is_active && (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                            {client.assigned_to_user_id ? (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<PersonRemoveIcon />}
                                onClick={() => handleUnassign(client.customer_id)}
                                disabled={assigningClientId === client.customer_id}
                              >
                                {assigningClientId === client.customer_id ? 'Procesando...' : 'Desasignar'}
                              </Button>
                            ) : null}
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Asignar a</InputLabel>
                              <Select
                                value=""
                                label="Asignar a"
                                onChange={(e) => handleAssign(client.customer_id, Number(e.target.value))}
                                disabled={assigningClientId === client.customer_id}
                              >
                                {ventasUsers.map((user) => (
                                  <MenuItem key={user.id} value={user.id}>
                                    {user.full_name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalClients}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Clientes por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Customers;
